import moment from "moment";
import {
  deriveDueBadge,
  excludeClosedWorkItems,
} from "@modules/command-center/utils";
import { CASE_STAGES, TASK_STAGES } from "@modules/command-center/constants";
import { AdvisorRoutes } from "@common/constants";
import { getSalesforceLightningUrl } from "@modules/salesforce/constants";
import {
  DueWindow,
  SortKey,
  UNRESOLVED_HOUSEHOLD,
  WorkItemFilters,
} from "./constants";
import {
  CasePatchBody,
  CasesTasksListResponse,
  TaskPatchBody,
  WorkItem,
  WorkItemKind,
  WorkItemPatchBody,
} from "./types";

// Fold { cases, tasks } into one kind-tagged, open-only list. `kind` is derived from
// which array a row came from, so it can never disagree with it.
const toWorkItems = (data?: CasesTasksListResponse): WorkItem[] => {
  if (!data) return [];
  const tag = (items: WorkItem[], kind: WorkItemKind) =>
    items.map((item) => ({ ...item, kind }));
  return excludeClosedWorkItems([
    ...tag(data.cases ?? [], "case"),
    ...tag(data.tasks ?? [], "task"),
  ]);
};

const stagesForKind = (kind: WorkItemKind): readonly string[] =>
  kind === "case" ? CASE_STAGES : TASK_STAGES;

const matchesSearch = (item: WorkItem, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [item.title, item.householdName, item.assigneeName].some((field) =>
    (field ?? "").toLowerCase().includes(needle)
  );
};

// Inclusion filter for the Cases/Tasks chips. An EMPTY set shows everything —
// un-toggling both is a fumble, not a request to see nothing.
const filterByKind = (
  items: WorkItem[],
  kinds: ReadonlySet<WorkItemKind>
): WorkItem[] =>
  kinds.size === 0 ? items : items.filter((item) => kinds.has(item.kind));

// Delegates overdue/today to deriveDueBadge so the filter and the row's badge can't
// disagree on a boundary date.
const matchesDueWindow = (
  item: WorkItem,
  window: DueWindow,
  now: moment.Moment
): boolean => {
  if (window === "any") return true;
  if (window === "noDate") return !item.dueDate;
  if (!item.dueDate) return false;

  // Pass `now` through, or the injected instant applies to next7/next30 while
  // overdue/today silently reads the real clock.
  const badge = deriveDueBadge(item.dueDate, now);
  if (!badge) return false;
  if (window === "overdue") return badge.tone === "overdue";
  if (window === "today") return badge.tone === "today";

  const days = moment(item.dueDate)
    .startOf("day")
    .diff(now.clone().startOf("day"), "days");
  const horizon = window === "next7" ? 7 : 30;
  return days >= 0 && days <= horizon;
};

// ANDed across categories, ORed within one. An empty category means no constraint.
const matchesFilters = (
  item: WorkItem,
  filters: WorkItemFilters,
  now: moment.Moment = moment()
): boolean => {
  if (filters.stages.length && !filters.stages.includes(item.stage)) {
    return false;
  }
  if (
    filters.assigneeNames.length &&
    !filters.assigneeNames.includes(item.assigneeName ?? "")
  ) {
    return false;
  }
  if (filters.householdIds.length) {
    const key = item.householdId ?? UNRESOLVED_HOUSEHOLD;
    if (!filters.householdIds.includes(key)) return false;
  }
  return matchesDueWindow(item, filters.dueWindow, now);
};

// Missing values sort last whichever way the present ones go — a case with no due date
// is not "the earliest".
const compareNullableLast = (
  a: string | null,
  b: string | null,
  direction: 1 | -1
): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a < b ? -direction : a > b ? direction : 0;
};

const byText = (a: string | null, b: string | null): number =>
  (a ?? "").localeCompare(b ?? "");

// Non-mutating and stable, so the list doesn't reshuffle on an unrelated change.
const sortWorkItems = (items: WorkItem[], key: SortKey): WorkItem[] => {
  const sorted = [...items];
  switch (key) {
    case "dueDateAsc":
      return sorted.sort(
        (a, b) =>
          compareNullableLast(a.dueDate, b.dueDate, 1) ||
          byText(a.title, b.title)
      );
    case "dueDateDesc":
      return sorted.sort(
        (a, b) =>
          compareNullableLast(a.dueDate, b.dueDate, -1) ||
          byText(a.title, b.title)
      );
    case "createdDateDesc":
      return sorted.sort((a, b) =>
        compareNullableLast(a.createdDate, b.createdDate, -1)
      );
    case "createdDateAsc":
      return sorted.sort((a, b) =>
        compareNullableLast(a.createdDate, b.createdDate, 1)
      );
    case "householdAsc":
      return sorted.sort(
        (a, b) =>
          byText(a.householdName, b.householdName) || byText(a.title, b.title)
      );
    case "titleAsc":
      return sorted.sort((a, b) => byText(a.title, b.title));
    default:
      return sorted;
  }
};

interface WorkItemQuery {
  search: string;
  kinds: ReadonlySet<WorkItemKind>;
  filters: WorkItemFilters;
  sort: SortKey;
}

interface WorkItemCounts {
  cases: number;
  tasks: number;
}

const countByKind = (items: WorkItem[]): WorkItemCounts => ({
  cases: items.filter((item) => item.kind === "case").length,
  tasks: items.filter((item) => item.kind === "task").length,
});

interface WorkItemQueryResult {
  items: WorkItem[];
  // Computed after search + filters but BEFORE the kind filter, so each chip shows what
  // toggling it on would reveal.
  counts: WorkItemCounts;
}

const applyWorkItemQuery = (
  items: WorkItem[],
  { search, kinds, filters, sort }: WorkItemQuery,
  now: moment.Moment = moment()
): WorkItemQueryResult => {
  const narrowed = items.filter(
    (item) => matchesSearch(item, search) && matchesFilters(item, filters, now)
  );
  return {
    items: sortWorkItems(filterByKind(narrowed, kinds), sort),
    counts: countByKind(narrowed),
  };
};

const buildCasesTasksSubtitle = ({ cases, tasks }: WorkItemCounts): string => {
  const plural = (count: number, noun: string) =>
    `${count} ${noun}${count === 1 ? "" : "s"}`;
  const total = cases + tasks;
  return [
    `${plural(total, "open item")}`,
    plural(cases, "case"),
    plural(tasks, "task"),
    "owners tracked in Salesforce",
  ].join(" · ");
};

interface WorkItemFilterOptions {
  stages: string[];
  assigneeNames: string[];
  households: { id: string; name: string }[];
}

// Derived from the loaded rows so an advisor only sees options that match something.
const buildFilterOptions = (items: WorkItem[]): WorkItemFilterOptions => {
  const stages = new Set<string>();
  const assigneeNames = new Set<string>();
  const households = new Map<string, string>();

  for (const item of items) {
    if (item.stage) stages.add(item.stage);
    if (item.assigneeName) assigneeNames.add(item.assigneeName);
    if (item.householdName) {
      households.set(
        item.householdId ?? UNRESOLVED_HOUSEHOLD,
        item.householdName
      );
    }
  }

  // Array.from, not spread: the TS target predates downlevelIteration.
  return {
    stages: Array.from(stages).sort(),
    assigneeNames: Array.from(assigneeNames).sort(),
    households: Array.from(households.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
};

const countActiveFilters = (filters: WorkItemFilters): number =>
  filters.stages.length +
  filters.assigneeNames.length +
  filters.householdIds.length +
  (filters.dueWindow === "any" ? 0 : 1);

// "AO" from "Dr. Amara Okafor" — honorifics dropped so initials stay two letters.
const HONORIFICS = new Set([
  "dr",
  "dr.",
  "mr",
  "mr.",
  "mrs",
  "mrs.",
  "ms",
  "ms.",
]);

const initialsFromName = (name?: string | null): string => {
  const parts = (name ?? "")
    .split(/[\s]+/)
    .filter(Boolean)
    .filter((part) => !HONORIFICS.has(part.toLowerCase()));
  const meaningful = parts.filter((part) => /[a-z]/i.test(part));
  const picked = [meaningful[0], meaningful[meaningful.length - 1]].filter(
    Boolean
  );
  const unique = meaningful.length === 1 ? [meaningful[0]] : picked;
  return unique
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2);
};

interface ChecklistProgress {
  label: string;
  ratio: number;
  percent: number;
}

const checklistProgress = (
  complete: number | null,
  total: number | null
): ChecklistProgress => {
  // Null on tasks (checklists are a case-only concept) and on a phased backend.
  const safeTotal = Math.max(0, total ?? 0);
  const safeComplete = Math.min(Math.max(0, complete ?? 0), safeTotal);
  const ratio = safeTotal === 0 ? 0 : safeComplete / safeTotal;
  return {
    label: `${safeComplete}/${safeTotal}`,
    ratio,
    percent: Math.round(ratio * 100),
  };
};

// Identifies one row across surfaces: the key the page expands by, and the value the
// Command Center card puts in ?item= so a deep link lands on this exact card.
const workItemKey = (item: Pick<WorkItem, "kind" | "id">): string =>
  `${item.kind}:${item.id}`;

// Deep link to one row on the Cases & Tasks page.
//
// Carries viewAs itself. An earlier version left it to "the sidebar and page", which
// was wrong — the sidebar only decorates nav links, so following this one while
// impersonating dropped the advisor, the page fell back to the caller's own book, and
// the linked row wasn't in it: nothing expanded and nothing scrolled.
const casesTasksItemHref = (
  item: Pick<WorkItem, "kind" | "id">,
  viewAsAdvisorEmail?: string
): string => {
  const params = new URLSearchParams({ item: workItemKey(item) });
  if (viewAsAdvisorEmail) {
    params.set("viewAs", viewAsAdvisorEmail);
  }
  return `${AdvisorRoutes.CasesAndTasks}?${params.toString()}`;
};

// The Cases & Tasks page itself, carrying impersonation the same way.
const casesTasksHref = (viewAsAdvisorEmail?: string): string =>
  viewAsAdvisorEmail
    ? `${AdvisorRoutes.CasesAndTasks}?viewAs=${encodeURIComponent(
        viewAsAdvisorEmail
      )}`
    : AdvisorRoutes.CasesAndTasks;

// The Salesforce record behind a row. `id` IS the Salesforce id, so no lookup needed.
const salesforceHrefFor = (item: Pick<WorkItem, "kind" | "id">): string =>
  getSalesforceLightningUrl(item.id, item.kind === "case" ? "Case" : "Task");

// Idempotency key for a write. The backend dedupes on it, so a caller must generate ONE
// and reuse it across retries of the same attempt. Falls back off crypto.randomUUID,
// which is undefined in insecure contexts and older jsdom.
const newRequestId = (): string => {
  const webCrypto = globalThis.crypto as Crypto | undefined;
  if (typeof webCrypto?.randomUUID === "function") {
    return webCrypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

// DatePicker normalises through moment.utc, so one formatter keeps edits from drifting a
// day in negative-offset zones.
const toApiDate = (date: moment.Moment | null): string | null =>
  date && date.isValid() ? date.format("YYYY-MM-DD") : null;

// Cases write `stage`, tasks write `status`, both landing on WorkItem.stage. One place,
// so the two paths can't diverge.
const applyPatchToWorkItem = (
  item: WorkItem,
  patch: WorkItemPatchBody
): WorkItem => {
  const next = { ...item };

  if (item.kind === "case") {
    const casePatch = patch as CasePatchBody;
    if (casePatch.stage !== undefined) next.stage = casePatch.stage;
  } else {
    const taskPatch = patch as TaskPatchBody;
    if (taskPatch.status !== undefined) next.stage = taskPatch.status;
  }

  // Both bodies name these identically.
  const shared = patch as CasePatchBody & TaskPatchBody;
  if (shared.description !== undefined) next.description = shared.description;
  if (shared.dueDate !== undefined) next.dueDate = shared.dueDate;
  // Only the id: the name comes via the mutation's `optimistic` override.
  if (shared.assigneeId !== undefined) next.assigneeId = shared.assigneeId;

  return next;
};

export {
  toWorkItems,
  stagesForKind,
  matchesSearch,
  filterByKind,
  matchesDueWindow,
  matchesFilters,
  sortWorkItems,
  applyWorkItemQuery,
  countByKind,
  buildCasesTasksSubtitle,
  buildFilterOptions,
  countActiveFilters,
  initialsFromName,
  checklistProgress,
  workItemKey,
  casesTasksItemHref,
  casesTasksHref,
  salesforceHrefFor,
  newRequestId,
  toApiDate,
  applyPatchToWorkItem,
};
export type {
  WorkItemQuery,
  WorkItemCounts,
  WorkItemQueryResult,
  WorkItemFilterOptions,
  ChecklistProgress,
};
